import useEmblaCarousel from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '#app/components/ui/button'
import { CarouselProgress } from '#app/components/ui/carousel-progress'
import { cn } from '#app/utils/misc'
import { normalizeCollectionMarketingPath } from '#app/utils/pz/brand/collectionEntity'
import { getCarouselSlideStyle } from '#app/utils/pz/carouselPalette'
import { getPublicProgramsIndexPath } from '#app/utils/pz/publicLearningRoutes'

export interface CollectionItem {
	title: string
	description?: string
	thumbnail?: string
	category?: string
	link?: string
	metadata?: string
	moduleCount?: number
	childCourseCount?: number
	childCourses?: Array<{
		title: string
		thumbnail?: string
	}>
	accessLevel?: 'free' | 'subscription'
}

export interface CollectionsCarouselProps {
	title?: string
	subtitle?: string
	items?: CollectionItem[]
	showViewAllButton?: boolean
	viewAllButtonText?: string
	viewAllButtonLink?: string
}

export function CollectionsCarousel({
	title: _title,
	subtitle: _subtitle,
	items = [],
	showViewAllButton: _showViewAllButton = false,
	viewAllButtonText: _viewAllButtonText,
	viewAllButtonLink: _viewAllButtonLink,
}: CollectionsCarouselProps) {
	const { t } = useTranslation()
	const collectionCatalogPath = getPublicProgramsIndexPath()
	const viewAllButtonLink = _viewAllButtonLink ?? collectionCatalogPath
	const hasSectionHeader = Boolean(_title || _subtitle || _showViewAllButton)
	const viewAllButtonText =
		_viewAllButtonText ?? t('common.collections.collectionsCarousel.viewAll')
	const sectionEyebrow = t('common.collections.collectionsCarousel.title')

	const normalizeHref = (href: string) => {
		if (!href) return href
		if (/^https?:\/\//i.test(href)) return href

		if (href === '/roadmaps' || href === '/collections') {
			return collectionCatalogPath
		}

		return normalizeCollectionMarketingPath(href)
	}
	const normalizedViewAllButtonLink = normalizeHref(viewAllButtonLink)

	// Embla carousel
	const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
	const [selectedIndex, setSelectedIndex] = React.useState(0)
	const [canScrollPrev, setCanScrollPrev] = React.useState(false)
	const [canScrollNext, setCanScrollNext] = React.useState(false)

	const onSelect = React.useCallback(() => {
		if (!emblaApi) return
		setSelectedIndex(emblaApi.selectedScrollSnap())
		setCanScrollPrev(emblaApi.canScrollPrev())
		setCanScrollNext(emblaApi.canScrollNext())
	}, [emblaApi])

	React.useEffect(() => {
		if (!emblaApi) return
		onSelect()
		emblaApi.on('select', onSelect)
		emblaApi.on('reInit', onSelect)
		return () => {
			emblaApi.off('select', onSelect)
		}
	}, [emblaApi, onSelect])

	const scrollPrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
	const scrollNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi])
	const scrollTo = React.useCallback(
		(index: number) => emblaApi?.scrollTo(index),
		[emblaApi],
	)

	if (items.length === 0) return null
	const MAX_CHILD_COURSE_AVATARS = 7

	return (
		<section className="w-full">
			<div
				className="relative w-full pt-4"
				style={getCarouselSlideStyle(selectedIndex)}
			>
				{hasSectionHeader ? (
					<div className="mx-auto max-w-7xl px-6 pt-6 sm:px-10 sm:pt-8 lg:px-16">
						<div className="flex items-center gap-3 pb-2 sm:pb-3">
							<p className="text-label">{sectionEyebrow}</p>
							{_showViewAllButton ? (
								<a
									href={normalizedViewAllButtonLink}
									aria-label={viewAllButtonText}
									className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current/28 bg-white/8 opacity-100 transition hover:bg-white/14"
								>
									<span className="sr-only">{viewAllButtonText}</span>
									<ArrowRight className="h-3.5 w-3.5" />
								</a>
							) : null}
						</div>
					</div>
				) : null}

				{/* Carousel viewport */}
				<div
					ref={emblaRef}
					className={cn(
						'overflow-hidden',
						hasSectionHeader ? 'pt-2 sm:pt-4' : '',
					)}
				>
					<div className="flex">
						{items.map((item, index) => {
							const href = item.link
								? normalizeHref(item.link)
								: viewAllButtonLink
							const isFree = item.accessLevel === 'free'
							const childCourses = (item.childCourses ?? []).slice(
								0,
								MAX_CHILD_COURSE_AVATARS,
							)
							const childCourseCount =
								typeof item.childCourseCount === 'number'
									? item.childCourseCount
									: item.moduleCount
							const hiddenChildCoursesCount =
								typeof childCourseCount === 'number'
									? Math.max(0, childCourseCount - childCourses.length)
									: 0

							return (
								<div
									key={`${item.title}-${index}`}
									className="min-w-0 shrink-0 grow-0 basis-full"
									style={getCarouselSlideStyle(index)}
								>
									<div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-0 md:grid-cols-2 md:gap-8">
										{/* Left: text content */}
										<div className="order-2 flex flex-col justify-center px-6 py-8 sm:px-10 md:order-1 md:py-16 lg:px-16 lg:py-20">
											{/* {item.category && (
												<Badge
													variant="outline"
													className="text-primary-foreground mb-4 w-fit border-current/50 text-xs font-medium opacity-100"
												>
													{item.category}
												</Badge>
											)} */}

											<h3 className="text-title-2">
												<a
													href={href}
													className="inline-flex items-center gap-2"
												>
													{item.title}
													{isFree && (
														<span className="text-label bg-primary/15 text-primary rounded px-1.5 py-0.5">
															{t('marketing.footer.freeBadge')}
														</span>
													)}
												</a>
											</h3>

											{item.description && (
												<p className="text-body mt-4 lg:text-lg">
													{item.description}
												</p>
											)}

											{(childCourseCount ||
												item.metadata ||
												childCourses.length > 0) && (
												<div className="mt-5 flex flex-wrap items-center gap-3">
													{childCourses.length > 0 && (
														<div className="flex items-center">
															{childCourses.map(
																(childCourse, childCourseIndex) => (
																	<div
																		key={`${item.title}-child-${childCourse.title}-${childCourseIndex}`}
																		className={cn(
																			'border-background bg-muted relative inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border text-xs font-semibold',
																			childCourseIndex > 0 && '-ml-3',
																		)}
																	>
																		{childCourse.thumbnail ? (
																			<img
																				src={childCourse.thumbnail}
																				alt={childCourse.title}
																				className="h-full w-full object-cover"
																			/>
																		) : (
																			<span className="text-muted-foreground">
																				{childCourse.title
																					.charAt(0)
																					.toUpperCase()}
																			</span>
																		)}
																	</div>
																),
															)}
															{hiddenChildCoursesCount > 0 && (
																<div className="border-background bg-muted -ml-3 inline-flex h-9 min-w-9 shrink-0 items-center justify-center rounded-full border-2 px-1 text-[11px] font-semibold">
																	+{hiddenChildCoursesCount}
																</div>
															)}
														</div>
													)}
													<p className="text-body-sm">
														{typeof childCourseCount === 'number'
															? t(
																	'common.collections.collectionsCarousel.courses',
																	{
																		count: childCourseCount,
																	},
																)
															: item.metadata}
													</p>
												</div>
											)}

											{/* CTA */}
											<div className="mt-6 flex justify-center md:justify-start">
												<Button
													asChild
													variant="outline"
													size="lg"
													className="hover:bg-secondary hover:text-gray/10 border-current/40 bg-transparent text-sm font-semibold"
												>
													<a href={href}>
														{isFree
															? t(
																	'common.collections.collectionsCarousel.viewFreeCollection',
																)
															: t(
																	'common.collections.collectionsCarousel.viewCollection',
																)}
													</a>
												</Button>
											</div>
										</div>

										{/* Right: image */}
										<div className="order-1 md:order-2">
											<div className="relative aspect-video w-full overflow-hidden rounded-md">
												{item.thumbnail ? (
													<img
														src={item.thumbnail}
														alt={item.title}
														className="h-full w-full rounded-md object-cover object-center"
													/>
												) : (
													<div className="bg-muted h-full w-full rounded-md" />
												)}
											</div>
										</div>
									</div>
								</div>
							)
						})}
					</div>
				</div>

				{/* Prev / Next arrows */}
				{items.length > 1 && (
					<>
						<button
							onClick={scrollPrev}
							disabled={!canScrollPrev}
							className="bg-background/90 hover:bg-background absolute top-1/2 left-2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md transition disabled:opacity-30 sm:left-4 sm:h-12 sm:w-12 md:flex"
							aria-label={t(
								'common.collections.collectionsCarousel.previousSlide',
							)}
						>
							<ArrowLeft className="text-foreground h-5 w-5" />
						</button>
						<button
							onClick={scrollNext}
							disabled={!canScrollNext}
							className="bg-background/90 hover:bg-background absolute top-1/2 right-2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md transition disabled:opacity-30 sm:right-4 sm:h-12 sm:w-12 md:flex"
							aria-label={t('common.collections.collectionsCarousel.nextSlide')}
						>
							<ArrowRight className="text-foreground h-5 w-5" />
						</button>
					</>
				)}

				{/* Dot indicators */}
				{items.length > 1 && (
					<div className="flex justify-center pt-2 pb-6 md:absolute md:bottom-4 md:left-1/2 md:z-10 md:-translate-x-1/2 md:p-0">
						<CarouselProgress
							count={items.length}
							activeIndex={selectedIndex}
							onSelect={scrollTo}
							getAriaLabel={(index) =>
								t('common.collections.collectionsCarousel.goToSlide', {
									slide: index + 1,
								})
							}
						/>
					</div>
				)}
			</div>
		</section>
	)
}
